import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../../components/AppShell.js";
import { Notice } from "../../components/ui.js";
import { fetchDraw } from "../../lib/draw.js";
import { fetchTieTeams } from "../../lib/tie.js";
import {
  fetchEventContacts,
  fetchEventNotes,
  fetchTournaments,
  findEvent,
} from "../../lib/tournaments.js";
import type { DrawFixture, Tournament, TournamentEvent } from "../../lib/types.js";

/**
 * One event, six screens.
 *
 * Registering, seeing who is in, reading the draw, entering a score, reading the results and
 * reading the table are six different reasons to open the app, and they were one page. A
 * player looking for the score of the match they just played had to scroll past a Register
 * button, a note field, an entry list and a team sheet to find it.
 *
 * So each is its own URL — which also means each is its own link. "Standings are here" is a
 * message somebody can send to the group chat now.
 *
 * Everything below is the shared furniture: one loader, one frame, one back link.
 */

export interface FoundEvent {
  tournament: Tournament;
  event: TournamentEvent;
}

/**
 * The event this screen is about, or a screen explaining why there isn't one.
 *
 * A render prop rather than a hook returning a union, because every one of the six screens
 * would otherwise repeat the same two guards, and the fifth copy is where they drift.
 */
export function EventGate({ children }: { children: (found: FoundEvent) => ReactNode }) {
  const { eventId } = useParams<{ eventId: string }>();

  const {
    data: tournaments,
    isPending,
    error,
  } = useQuery({ queryKey: ["tournaments"], queryFn: fetchTournaments });

  const found = eventId && tournaments ? findEvent(tournaments, eventId) : null;

  if (isPending) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  // Missing and forbidden look the same from here, and that is on purpose: RLS returns no
  // row either way, so claiming "you can't see this one" would be a guess.
  if (error || !found) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Link to="/events" className="micro mb-4 inline-block underline">
          ← All events
        </Link>
        <Notice tone="attention">
          {error ? "Couldn't load this event. Try again." : "That event isn't here any more."}
        </Notice>
      </main>
    );
  }

  return <>{children(found)}</>;
}

/**
 * The frame every screen but the hub sits in: back to the event, the screen's own name, and
 * the event's name underneath it so a shared link says what it is about.
 */
export function EventScreen({
  event,
  title,
  children,
}: {
  event: TournamentEvent;
  title: string;
  children: ReactNode;
}) {
  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link to={`/events/${event.id}`} className="micro mb-4 inline-block underline">
        ← {event.name}
      </Link>

      {/* The same header every top-level screen has: the event as the eyebrow, what this
          screen is as the title. These were the last screens still on the old small title. */}
      <PageHeader eyebrow={event.name} title={title} />

      {children}
    </main>
  );
}

/**
 * The draw, which most of these screens read. One query key, so they share one fetch.
 *
 * `refetchInterval` is for the court board, which is left on a screen nobody is touching and
 * has to notice a score entered on somebody's phone across the hall.
 */
export function useDraw(eventId: string, options: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: ["draw", eventId],
    queryFn: () => fetchDraw(eventId),
    ...options,
    // A polling query stops polling when the window loses focus, which is the sensible
    // default everywhere except the one screen that has this option set: a board on a
    // television is never the focused window, and one that stopped updating the moment
    // somebody clicked elsewhere would be a board showing a match that finished an hour ago.
    ...(options.refetchInterval ? { refetchIntervalInBackground: true } : {}),
  });
}

export function useTieTeams(event: TournamentEvent) {
  return useQuery({
    queryKey: ["tie-teams", event.id],
    queryFn: () => fetchTieTeams(event.id),
    enabled: event.format === "team_tie",
  });
}

/**
 * Notes are yours, plus everyone's if you organise — `event_notes` decides that, not the
 * screen. Asked for on both the register screen and the entry list, and the same key on both
 * means the second one costs nothing.
 */
export function useEventNotes(eventId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["event-notes", eventId],
    queryFn: () => fetchEventNotes(eventId),
    enabled,
  });
}

/**
 * How to reach each entrant — organisers only. `event_contacts` answers nobody else, so the
 * screen asks only when it would be answered.
 */
export function useEventContacts(eventId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["event-contacts", eventId],
    queryFn: () => fetchEventContacts(eventId),
    enabled,
  });
}

/** A face and a name, which is how everybody appears on every one of these screens. */
export interface Person {
  name: string;
  url: string | null;
}

/**
 * Everybody entered, by id.
 *
 * A fixture can outlive an entry — somebody taken out after the draw is still on it — so
 * every lookup has a fallback rather than a blank. Carrying the avatar alongside the name is
 * why this is a map of people rather than of strings: the entry list already has both, and a
 * draw of names with no faces reads as a spreadsheet.
 */
export type Roster = Map<string, Person>;

export const rosterOf = (event: TournamentEvent): Roster =>
  new Map(
    event.entries.map((entry) => [
      entry.memberId,
      { name: entry.displayName, url: entry.avatarUrl },
    ]),
  );

const missing: Person = { name: "withdrawn", url: null };

export const sidePeople = (ids: string[] | null | undefined, roster: Roster): Person[] =>
  (ids ?? []).map((id) => roster.get(id) ?? missing);

export const sideNames = (ids: string[] | null | undefined, roster: Roster) =>
  sidePeople(ids, roster)
    .map((person) => person.name)
    .join(" & ");

/** Every fixture this member is drawn in, in playing order. */
export const fixturesFor = (fixtures: DrawFixture[], memberId: string | null) =>
  memberId === null
    ? []
    : fixtures.filter(
        (fixture) => fixture.playersA?.includes(memberId) || fixture.playersB?.includes(memberId),
      );
